import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";

const SITE_URL = "https://episodekit.net";

const useJsonLd = (jsonLd) => {
  useEffect(() => {
    if (!jsonLd) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-jsonld", "about");
    el.text = JSON.stringify(jsonLd);
    document.head.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [JSON.stringify(jsonLd)]);
};

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-14">
    <h2 className="text-2xl md:text-3xl font-unbounded font-semibold mb-4">
      {title}
    </h2>
    <div className="text-neutral-300 leading-relaxed space-y-4 text-[15px] md:text-base">
      {children}
    </div>
  </section>
);

export default function About() {
  useSEO({
    title: "About EpisodeKit — Editorial Standards & Product Transparency",
    description:
      "About EpisodeKit — what the product is, how our content is created, how we research product comparisons, our editorial standards, and how to contact us.",
    path: "/about",
    image: "/og/episodekit-og-home.jpg",
  });

  useJsonLd([
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      url: `${SITE_URL}/about`,
      name: "About EpisodeKit",
      description:
        "About EpisodeKit — what the product is, how content is created, product comparison methodology, editorial standards, and contact.",
      isPartOf: {
        "@type": "WebSite",
        url: SITE_URL,
        name: "EpisodeKit",
      },
      about: {
        "@type": "Organization",
        name: "EpisodeKit",
        url: SITE_URL,
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: "About",
          item: `${SITE_URL}/about`,
        },
      ],
    },
  ]);

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white"
      data-testid="about-page"
    >
      <Navbar />

      <main className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav
            aria-label="Breadcrumb"
            className="text-sm text-neutral-500 mb-8 flex items-center gap-2"
          >
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-neutral-300">About</span>
          </nav>

          {/* Hero */}
          <header className="mb-14 md:mb-16">
            <p className="text-purple-400 uppercase text-xs tracking-widest mb-4 font-semibold">
              About the product · Editorial standards
            </p>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-unbounded font-bold leading-[1.05] mb-6"
              data-testid="about-h1"
            >
              About EpisodeKit
            </h1>
            <p className="text-neutral-300 text-lg leading-relaxed max-w-2xl">
              A short, honest page about what EpisodeKit is, how our content
              is created and researched, and the standards we hold ourselves
              to when writing about our own product and other people&apos;s
              products.
            </p>
          </header>

          {/* Table of contents */}
          <nav
            aria-label="On this page"
            className="mb-14 rounded-2xl border border-neutral-800 bg-neutral-900/40 p-5 md:p-6"
          >
            <p className="text-[11px] uppercase tracking-widest text-purple-300 font-semibold mb-3">
              On this page
            </p>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-neutral-300">
              <li>
                <a href="#what-is-episodekit" className="hover:text-white">
                  What EpisodeKit is
                </a>
              </li>
              <li>
                <a href="#content" className="hover:text-white">
                  How our content is created
                </a>
              </li>
              <li>
                <a href="#comparisons" className="hover:text-white">
                  How product comparisons are researched
                </a>
              </li>
              <li>
                <a href="#standards" className="hover:text-white">
                  Our editorial standards
                </a>
              </li>
              <li>
                <a href="#contact" className="hover:text-white">
                  Contact
                </a>
              </li>
            </ol>
          </nav>

          <Section id="what-is-episodekit" title="What EpisodeKit is">
            <p>
              EpisodeKit is a podcast content distribution system. You
              upload one podcast episode and it produces a pack of
              publish-ready written assets — transcript, show notes, SEO
              blog post, chapters, discovery titles, YouTube description,
              pinned comment, quotes, Shorts hooks, platform-specific
              social captions, and newsletter-ready copy — all built from
              the same source episode.
            </p>
            <p>
              EpisodeKit exists because the biggest bottleneck for most
              podcasters isn&apos;t recording — it&apos;s everything that
              happens after the recording. The audio ships to Spotify or
              Apple and effectively disappears from the searchable web.
              Turning every episode into structured, publish-ready written
              assets is what turns a podcast into a compounding content
              engine on your own domain.
            </p>
            <p>
              What EpisodeKit does <strong>not</strong> do: it does not
              automatically edit final short-form video clips, does not
              publish directly to social platforms, does not send
              newsletters, and does not record remote podcasts. It
              produces the content layer and hands it to your publishing
              tools of choice. For the concrete product surface, see the{" "}
              <Link to="/pricing" className="text-purple-300 hover:text-white underline underline-offset-4 decoration-purple-500/40">
                pricing page
              </Link>{" "}
              or the public{" "}
              <Link to="/episode-examples/mfm-10k-to-1m" className="text-purple-300 hover:text-white underline underline-offset-4 decoration-purple-500/40">
                MFM example deliverable
              </Link>.
            </p>
          </Section>

          <Section id="content" title="How EpisodeKit content is created">
            <p>
              Our articles combine four things: hands-on product
              knowledge of EpisodeKit, publicly available documentation
              from tools and platforms we write about, real EpisodeKit
              output examples generated from actual podcast episodes,
              and external authoritative sources where a specific claim
              needs a source.
            </p>
            <p>
              Where possible we illustrate concepts with real
              deliverables generated by EpisodeKit itself — such as the
              public{" "}
              <Link to="/episode-examples/mfm-10k-to-1m" className="text-purple-300 hover:text-white underline underline-offset-4 decoration-purple-500/40">
                MFM example
              </Link>
              . Those figures are labelled &ldquo;Real EpisodeKit
              output&rdquo; in-line so readers can distinguish first-party
              evidence from illustrative diagrams.
            </p>
          </Section>

          <Section
            id="comparisons"
            title="How product comparisons are researched"
          >
            <p>
              When we compare EpisodeKit to other products (for example
              in our{" "}
              <Link to="/blog/best-podcast-repurposing-tools-2026" className="text-purple-300 hover:text-white underline underline-offset-4 decoration-purple-500/40">
                best podcast repurposing tools comparison
              </Link>{" "}
              or the{" "}
              <Link to="/blog/episodekit-vs-castmagic" className="text-purple-300 hover:text-white underline underline-offset-4 decoration-purple-500/40">
                EpisodeKit vs Castmagic head-to-head
              </Link>
              ), we work to these rules:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                Official product documentation and pricing pages are the
                primary source. Third-party listicles are not.
              </li>
              <li>
                We apply the same evaluation criteria to every product,
                including EpisodeKit.
              </li>
              <li>
                We disclose visibly, near the top of every comparison
                article, that EpisodeKit is our product.
              </li>
              <li>
                We do not claim to have run hands-on tests of a
                competitor product unless that testing has actually
                occurred and is documented.
              </li>
              <li>
                We include each product&apos;s current published pricing
                page in its native currency and billing period, with the
                date the information was last checked.
              </li>
              <li>
                Prices and features change. Every comparison article
                links to each provider&apos;s official pricing page so
                readers can verify the current state before purchasing.
              </li>
            </ul>
          </Section>

          <Section id="standards" title="Our editorial standards">
            <p>
              A short, honest summary of what we try to do and what we
              try to avoid.
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                Factual claims should be sourced or softened. If a number
                or benchmark cannot be sourced or reproduced from real
                EpisodeKit data, it does not go in the article.
              </li>
              <li>
                No guaranteed ranking claims. No article on this site
                promises Google, YouTube, or AI-answer-engine rankings.
              </li>
              <li>
                No fabricated benchmarks. If we describe a workflow, we
                describe what actually happens, not what we&apos;d like
                to be able to say.
              </li>
              <li>
                No fake reviews or star ratings on comparison articles.
                Category winners are declared based on published
                evaluation criteria, not invented scores.
              </li>
              <li>
                Product limitations are disclosed. EpisodeKit&apos;s own
                limitations (no automatic video editing, no direct
                social publishing, no email sending, no remote
                recording) are stated in every article that describes
                the product.
              </li>
              <li>
                Corrections happen when needed. If a claim was wrong or
                a competitor&apos;s pricing changed since a comparison
                was written, the article is updated and the
                dateModified is bumped accordingly.
              </li>
            </ul>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              For editorial corrections, product questions, or press,
              email us at{" "}
              <a
                href="mailto:support@episodekit.net"
                className="text-purple-300 hover:text-white underline underline-offset-4 decoration-purple-500/40"
                data-testid="about-contact-email"
              >
                support@episodekit.net
              </a>
              .
            </p>
            <p className="text-sm text-neutral-500">
              For product bugs or account issues, the same address is the
              fastest channel.
            </p>
          </Section>

          <div className="pt-8 border-t border-neutral-800/60">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-white border border-neutral-700 hover:border-purple-500/60 rounded-full px-5 py-3 transition-colors"
              data-testid="about-back-to-blog"
            >
              ← Read the EpisodeKit blog
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
